import { jsxs, jsx } from "react/jsx-runtime";
import React, { forwardRef } from "react";
import styled, { css } from "styled-components";
import { useFocusRing } from "@react-aria/focus";
import { textStyles } from "./textStyles.js";
import { token } from "./token.js";
import { Checkbox } from "./Checkbox.js";
import { Thumbnail } from "./Thumbnail.js";
import { useId } from "./useId.js";
import { Switch } from "./Switch.js";
import { TruncatedText } from "./TruncatedText.js";
import { useItemDragAndDropContext } from "./context10.js";
const Item = forwardRef(({
  children,
  subtext,
  switchProps,
  thumbnail,
  rightElements,
  onClick,
  isDisabled = false,
  isSelected = false,
  checkedVariant,
  isChecked = false,
  isIndeterminateCheckbox = false,
  ...props
}, ref) => {
  const labelId = useId("list-item-label");
  const {
    isFocusVisible,
    focusProps
  } = useFocusRing();
  const itemDndContext = useItemDragAndDropContext();
  const rightElementsRef = React.useRef(null);
  const SwitchClassName = (switchProps == null ? void 0 : switchProps.onChange) ? "" : Switch.pointerEventsClassname;
  const className = [Checkbox.pointerEventsClassname, SwitchClassName, Thumbnail.pointerEventsClassname].join(" ");
  const isClickable = typeof onClick === "function";
  const isOnclickEnabled = isClickable && !isDisabled;
  function handleKeyDown(event) {
    if (event.key !== "ArrowDown" && event.key !== "ArrowUp" && event.key !== "ArrowLeft" && event.key !== "ArrowRight") {
      event.stopPropagation();
    }
  }
  return /* @__PURE__ */ jsxs(ItemStyled, {
    $isDragging: (itemDndContext == null ? void 0 : itemDndContext.isDragging) ?? false,
    $isSelected: isSelected,
    $isActive: isClickable,
    tabIndex: isOnclickEnabled ? 0 : void 0,
    onClick: isOnclickEnabled ? onClick : void 0,
    onKeyDown: (event) => {
      if (typeof onClick === "function" && (event.key === "Enter" || event.key === " ")) {
        event.preventDefault();
        onClick();
      }
    },
    $isDisabled: isDisabled,
    className,
    $isFocusVisible: isFocusVisible,
    ref,
    ...props,
    ...focusProps,
    children: [checkedVariant === "checkbox" && /* @__PURE__ */ jsx(CheckboxContainer, {
      onClick: (event) => {
        event.stopPropagation();
      },
      onKeyDown: (event) => {
        event.stopPropagation();
      },
      children: /* @__PURE__ */ jsx(Checkbox, {
        isChecked,
        isIndeterminate: isIndeterminateCheckbox,
        isDisabled,
        "aria-labelledby": labelId,
        onChange: onClick
      })
    }), thumbnail && /* @__PURE__ */ jsx(ThumbnailContainer, {
      $isDisabled: isDisabled,
      children: thumbnail
    }), /* @__PURE__ */ jsxs(Content, {
      id: labelId,
      children: [/* @__PURE__ */ jsx(TruncatedText, {
        children
      }), subtext ? /* @__PURE__ */ jsx(Subtext, {
        $isDisabled: isDisabled,
        children: /* @__PURE__ */ jsx(TruncatedText, {
          children: subtext
        })
      }) : null]
    }), rightElements && /* @__PURE__ */ jsx(RightElements, {
      ref: rightElementsRef,
      onClick: (event) => {
        var _a;
        const hasButtonsOrLinks = (_a = rightElementsRef == null ? void 0 : rightElementsRef.current) == null ? void 0 : _a.querySelectorAll("a, button").length;
        if (hasButtonsOrLinks) {
          event.stopPropagation();
        }
      },
      onKeyDown: handleKeyDown,
      children: rightElements
    }), checkedVariant === "switch" && /* @__PURE__ */ jsx(RightElements, {
      onClick: (event) => {
        event.stopPropagation();
      },
      onKeyDown: (event) => {
        event.stopPropagation();
      },
      children: /* @__PURE__ */ jsx(Switch, {
        isChecked,
        isDisabled,
        "aria-labelledby": labelId,
        onChange: onClick,
        ...switchProps
      })
    }), (itemDndContext == null ? void 0 : itemDndContext.dragIndicator) && /* @__PURE__ */ jsx(RightElements, {
      children: itemDndContext == null ? void 0 : itemDndContext.dragIndicator
    })]
  });
});
const ItemStyled = styled.div.withConfig({
  displayName: "ItemStyled",
  componentId: "sc-mhz3sb-0"
})(["--list-item-right-elements-color:", ";display:flex;align-items:center;margin-right:calc(", " * -1);margin-left:calc(", " * -1);padding:", " ", ";border-radius:var(--ds-list-item-border-radius-override,", ");&:hover{--list-item-concealead-action-opacity:1;--list-item-concealead-action-width:auto;}", " ", " ", ""], token.gray200, token.spacing4, token.spacing4, token.spacing3, token.spacing4, token.radiusLarge, (props) => props.$isActive && css(["--list-item-right-elements-color:", ";outline:none;cursor:pointer;&&:hover{--list-item-right-elements-color:", ";background:", ";}", " &&:active{background:", ";box-shadow:none;}", ""], token.gray200, token.gray600, token.gray25, props.$isFocusVisible && css(["--list-item-right-elements-color:", ";background:", ";&&,&&:hover{box-shadow:var( --ds-list-item-focus-ring-override,inset 0 0 0 2px ", ",inset 0 0 0 4px ", " );}"], token.gray600, token.gray25, token.gray50, token.gray10), token.gray50, props.$isDisabled && css(["--list-item-right-elements-color:", ";color:", ";pointer-events:none;"], token.gray200, token.colorTextDisabled)), (props) => props.$isSelected && css(["--list-item-right-elements-color:", ";&&,&&:hover{background:", ";}"], token.gray600, token.gray50), (props) => props.$isDragging && css(["--list-item-right-elements-color:", ";background:", ";box-shadow:", ";cursor:grabbing;"], token.gray600, token.gray25, token.elevation4));
const CheckboxContainer = styled.div.withConfig({
  displayName: "CheckboxContainer",
  componentId: "sc-mhz3sb-1"
})(["flex:none;margin-right:", ";"], token.spacing4);
const ThumbnailContainer = styled.div.withConfig({
  displayName: "ThumbnailContainer",
  componentId: "sc-mhz3sb-2"
})(["flex:none;margin-right:", ";", ""], token.spacing4, (props) => props.$isDisabled && "opacity: 50%;");
const Content = styled.div.withConfig({
  displayName: "Content",
  componentId: "sc-mhz3sb-3"
})(["", " flex:1;min-width:0;"], textStyles.uiTextM);
const Subtext = styled.div.withConfig({
  displayName: "Subtext",
  componentId: "sc-mhz3sb-4"
})(["", " color:", ";"], textStyles.uiTextS, (props) => props.$isDisabled ? token.colorTextDisabled : token.colorTextMuted);
const RightElements = styled.div.withConfig({
  displayName: "RightElements",
  componentId: "sc-mhz3sb-5"
})(["display:flex;flex:none;gap:", ";align-items:center;margin-left:", ";color:var(--list-item-right-elements-color);"], token.spacing3, token.spacing4);
export {
  Item
};
//# sourceMappingURL=Item.js.map
